import type { PayloadAction } from '@reduxjs/toolkit';
import type { UpdateQuoteLineResponse } from '@snap-types/features/alpha/actions/update-quote-line';

import { buildCalculatedData } from '../../logic/build-calculated-data';
import { WritableAutoConfiguratorState } from '../slice';
import { revalidateFieldValuePreservingParseErrors } from '../validation/apply-validation';

type SetCalculationResultsPayload = {
  lineItemUuid: string;
  result: UpdateQuoteLineResponse;
};

export function setCalculationResults(
  state: WritableAutoConfiguratorState,
  action: PayloadAction<SetCalculationResultsPayload>,
) {
  const { lineItemUuid, result } = action.payload;
  const {
    quotePrices: quotePriceResults,
    status,
    evaluationStatus,
    syncStatus,
    snapQuoteLineUuid,
    lastAppliedAt,
    lastApplyFailedAt,
    ...quoteLineResult
  } = result;
  const lineItem = state.lineItems[lineItemUuid];

  if (!lineItem) {
    return;
  }

  const calculatedData = buildCalculatedData(result);

  delete state.calculationErrorsByLineItemUuid[lineItemUuid];
  lineItem.status = status;
  lineItem.evaluationStatus = evaluationStatus;
  lineItem.syncStatus = syncStatus;
  lineItem.snapQuoteLineUuid = snapQuoteLineUuid;
  lineItem.lastAppliedAt = lastAppliedAt;
  lineItem.lastApplyFailedAt = lastApplyFailedAt;
  lineItem.calculatedData = calculatedData;
  lineItem.description.value = quoteLineResult.description ?? '';

  for (let i = 0; i < lineItem.quantityBreakUuids.length; i++) {
    const qbUuid = lineItem.quantityBreakUuids[i];
    const qb = state.quantityBreaks[qbUuid];
    const priceResult = quotePriceResults[i];
    if (qb && priceResult) {
      qb.evaluationStatus = priceResult.evaluationStatus;
      qb.calculatedData = calculatedData?.quotePrices[i] || null;

      /**
       * @NOTE(shawk): re-validate quantity field now that we have fresh
       * calculation results, as there may be a new minimumQuantity to validate
       * against
       */
      revalidateFieldValuePreservingParseErrors(
        state,
        { model: 'quantityBreak', key: 'quantity' },
        { model: 'quantityBreak', key: 'quantity', uuid: qbUuid },
      );
    }
  }
}
